import qz from 'qz-tray';

// ── Certificate overrides (unsigned / free mode — shows confirm dialog) ──
qz.security.setCertificatePromise((resolve) => resolve(null));
qz.security.setSignaturePromise(() => (resolve) => resolve(null));

// Suppress uncaught fetch errors from QZ Tray's internal WebSocket setup
// when QZ Tray isn't running. Without this, React shows a red error overlay.
if (typeof window !== 'undefined') {
  const origListener = window.onunhandledrejection;
  window.addEventListener('unhandledrejection', (e) => {
    const msg = String(e?.reason?.message || e?.reason || '');
    if (/qz|websocket.*close|Failed to fetch/i.test(msg)) {
      e.preventDefault();
      return;
    }
    if (origListener) origListener(e);
  });
}

// ── WebSocket lifecycle ──

export async function connect() {
  if (qz.websocket.isActive()) return;
  try {
    await qz.websocket.connect();
  } catch (e) {
    const msg = e?.message || String(e);
    if (/fetch|network|connect|ECONNREFUSED/i.test(msg)) {
      throw new Error('QZ Tray is not running. Install and start QZ Tray on this computer.');
    }
    throw e;
  }
}

export async function disconnect() {
  if (!qz.websocket.isActive()) return;
  await qz.websocket.disconnect();
}

export function isConnected() {
  return qz.websocket.isActive();
}

export function setClosedCallback(fn) {
  qz.websocket.setClosedCallbacks(fn);
}

// ── Printers ──

export async function listPrinters() {
  await connect();
  return qz.printers.find();
}

// ── Raw ZPL printing ──

export async function printRawZpl(printerName, zplString) {
  if (!printerName) throw new Error('No printer name configured.');
  await connect();
  const config = qz.configs.create(printerName);
  await qz.print(config, [{
    type: 'raw',
    format: 'plain',
    data: zplString,
  }]);
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

const CODE128_PATTERNS = [
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312', '132212', '221213',
  '221312', '231212', '112232', '122132', '122231', '113222', '123122', '123221', '223211', '221132',
  '221231', '213212', '223112', '312131', '311222', '321122', '321221', '312212', '322112', '322211',
  '212123', '212321', '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121', '313121', '211331',
  '231131', '213113', '213311', '213131', '311123', '311321', '331121', '312113', '312311', '332111',
  '314111', '221411', '431111', '111224', '111422', '121124', '121421', '141122', '141221', '112214',
  '112412', '122114', '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112', '421211', '212141',
  '214121', '412121', '111143', '111341', '131141', '114113', '114311', '411113', '411311', '113141',
  '114131', '311141', '411131', '211412', '211214', '211232', '2331112',
];

function normalizeBarcodeText(value) {
  const text = String(value || '').trim();
  return text
    .replace(/[^\x20-\x7e]/g, '')
    .slice(0, 48) || ' ';
}

function code128Svg(value) {
  const text = normalizeBarcodeText(value);
  const codes = [104, ...Array.from(text).map((ch) => ch.charCodeAt(0) - 32)];
  let checksum = codes[0];
  for (let i = 1; i < codes.length; i += 1) checksum += codes[i] * i;
  codes.push(checksum % 103, 106);

  const quietModules = 10;
  let x = quietModules;
  const rects = [];
  codes.forEach((code) => {
    const pattern = CODE128_PATTERNS[code];
    if (!pattern) return;
    Array.from(pattern).forEach((widthChar, index) => {
      const width = Number(widthChar);
      if (index % 2 === 0) rects.push(`<rect x="${x}" y="0" width="${width}" height="40"/>`);
      x += width;
    });
  });

  const totalWidth = x + quietModules;
  return `<svg class="barcode-svg" viewBox="0 0 ${totalWidth} 40" preserveAspectRatio="none" aria-hidden="true">${rects.join('')}</svg>`;
}

function mmFromDots(value, axisDots, axisMm) {
  const n = Number(value || 0);
  const dots = Math.max(1, Number(axisDots || 1));
  const mm = Number(axisMm || 0);
  return Number(((n / dots) * mm).toFixed(3));
}

function ptFromDots(value, labelHeightDots, labelHeightMm, fallbackPt) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return fallbackPt;
  return Number((mmFromDots(n, labelHeightDots, labelHeightMm) * 2.83465).toFixed(2));
}

function fitFontPt(value, basePt) {
  const length = String(value || '').trim().length;
  if (length > 34) return Math.max(5, Number((basePt * 0.62).toFixed(2)));
  if (length > 28) return Math.max(5.4, Number((basePt * 0.72).toFixed(2)));
  if (length > 22) return Math.max(5.8, Number((basePt * 0.82).toFixed(2)));
  return basePt;
}

function laserLineHtml(className, value, style, cfg, yField, fontField) {
  const safeStyle = sanitizePrintStickerStyle(style);
  const text = applyTextCase(value, safeStyle);
  const leftMm = mmFromDots(safeStyle.marginX, safeStyle.labelWidth, cfg.labelWidthMm);
  const topMm = mmFromDots(safeStyle[yField], safeStyle.labelHeight, cfg.labelHeightMm);
  const widthMm = Math.max(8, cfg.labelWidthMm - (leftMm * 2));
  const basePt = ptFromDots(safeStyle[fontField], safeStyle.labelHeight, cfg.labelHeightMm, 8);
  const fontPt = fitFontPt(text, basePt);
  return `
    <div class="laser-line ${className}" style="left:${leftMm}mm;top:${topMm}mm;width:${widthMm}mm;font-size:${fontPt}pt;text-align:${safeStyle.textAlign};">
      ${escapeHtml(text)}
    </div>
  `;
}

function laserBarcodeHtml(value, style, cfg) {
  const safeStyle = sanitizePrintStickerStyle(style);
  const text = applyTextCase(value, safeStyle);
  const marginMm = mmFromDots(safeStyle.marginX, safeStyle.labelWidth, cfg.labelWidthMm);
  const contentWidthMm = Math.max(20, cfg.labelWidthMm - (marginMm * 2));
  const estimatedWidthMm = Math.min(
    contentWidthMm,
    Math.max(28, mmFromDots(String(text || '').length * safeStyle.barcodeModuleWidth * 11, safeStyle.labelWidth, cfg.labelWidthMm))
  );
  let leftMm = marginMm;
  if (safeStyle.barcodeAlign === 'center') leftMm = Math.max(0, (cfg.labelWidthMm - estimatedWidthMm) / 2);
  if (safeStyle.barcodeAlign === 'right') leftMm = Math.max(0, cfg.labelWidthMm - marginMm - estimatedWidthMm);
  const topMm = mmFromDots(safeStyle.barcodeY, safeStyle.labelHeight, cfg.labelHeightMm);
  const heightMm = Math.max(4, mmFromDots(safeStyle.barcodeHeight, safeStyle.labelHeight, cfg.labelHeightMm));
  return `
    <div class="bars" style="left:${Number(leftMm.toFixed(3))}mm;top:${topMm}mm;width:${Number(estimatedWidthMm.toFixed(3))}mm;height:${heightMm}mm;">
      ${code128Svg(text)}
    </div>
  `;
}

function sanitizeClassName(value) {
  return String(value || '')
    .split(/\s+/)
    .map((part) => part.trim().toLowerCase())
    .filter((part) => /^[a-z0-9_-]{1,32}$/.test(part))
    .slice(0, 6)
    .join(' ');
}

function renderControlledTemplateHtml(template, fallbackBarcodeText) {
  const raw = String(template || '').trim();
  if (!raw || typeof window === 'undefined' || typeof window.DOMParser !== 'function') return '';
  const normalized = raw.replace(/<barcode\b([^>]*)\/>/gi, '<barcode$1></barcode>');
  const doc = new window.DOMParser().parseFromString(`<div>${normalized}</div>`, 'text/html');
  const allowed = new Set(['DIV', 'P', 'SPAN', 'STRONG', 'B', 'EM', 'BR', 'BARCODE']);

  const walk = (node) => {
    if (!node) return '';
    if (node.nodeType === window.Node.TEXT_NODE) return escapeHtml(node.textContent || '');
    if (node.nodeType !== window.Node.ELEMENT_NODE) return '';
    if (!allowed.has(node.tagName)) {
      return Array.from(node.childNodes || []).map(walk).join('');
    }
    if (node.tagName === 'BR') return '<br />';
    if (node.tagName === 'BARCODE') {
      const value = node.getAttribute('value') || node.textContent || fallbackBarcodeText || '';
      return `<div class="template-barcode">${code128Svg(value)}</div>`;
    }
    const tag = node.tagName === 'P' ? 'div' : node.tagName.toLowerCase();
    const className = sanitizeClassName(node.getAttribute('class'));
    const classAttr = className ? ` class="${escapeHtml(className)}"` : '';
    const body = Array.from(node.childNodes || []).map(walk).join('');
    return `<${tag}${classAttr}>${body}</${tag}>`;
  };

  return Array.from(doc.body.firstElementChild?.childNodes || []).map(walk).join('');
}

function renderStyledLaserLabelHtml(payload = {}, style = {}, cfg = { labelWidthMm: 50.8, labelHeightMm: 25.4 }) {
  const safeStyle = sanitizePrintStickerStyle(style);
  const templateHtml = renderControlledTemplateHtml(payload.htmlTemplate, payload.barcodeText);
  if (templateHtml) {
    return `<div class="html-template">${templateHtml}</div>`;
  }
  return `
    ${laserLineHtml('l1', payload.line1, safeStyle, cfg, 'line1Y', 'line1FontSize')}
    ${laserLineHtml('l2', payload.line2, safeStyle, cfg, 'line2Y', 'line2FontSize')}
    ${laserLineHtml('l3', payload.line3, safeStyle, cfg, 'line3Y', 'line3FontSize')}
    ${laserBarcodeHtml(payload.barcodeText, safeStyle, cfg)}
  `;
}

export function printLaserSticker({ line1, line2, line3, barcodeText, style = {} } = {}) {
  const cfg = { labelWidthMm: 50.8, labelHeightMm: 25.4 };
  const win = window.open('', 'fvs-laser-sticker-print', 'width=720,height=520');
  if (!win) throw new Error('Popup blocked. Allow popups to print laser preview.');
  win.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>FVS Sticker Test Print</title>
        <style>
          @page { size: A4; margin: 12mm; }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            font-family: Arial, Helvetica, sans-serif;
            color: #111827;
          }
          .sheet {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            min-height: 100vh;
          }
          .label {
            width: 2in;
            height: 1in;
            border: 1px solid #111827;
            position: relative;
            overflow: hidden;
          }
          .laser-line {
            position: absolute;
            font-weight: 700;
            line-height: 1;
            white-space: nowrap;
            overflow: hidden;
          }
          .bars {
            position: absolute;
            overflow: hidden;
          }
          .barcode-svg { display: block; width: 100%; height: 100%; fill: #111827; }
          .html-template {
            position: absolute;
            inset: 0;
            padding: 1.6mm 2mm;
            font-weight: 800;
            line-height: 1.05;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .html-template .line { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .html-template > * { margin-top: 0; margin-bottom: 0; }
          .html-template .fs-6 { font-size: 6pt; }
          .html-template .fs-7 { font-size: 7pt; }
          .html-template .fs-8 { font-size: 8pt; }
          .html-template .fs-9 { font-size: 9pt; }
          .html-template .fs-10 { font-size: 10pt; }
          .html-template .fs-11 { font-size: 11pt; }
          .html-template .fs-12 { font-size: 12pt; }
          .html-template .fs-14 { font-size: 14pt; }
          .html-template .fs-16 { font-size: 16pt; }
          .html-template .fs-18 { font-size: 18pt; }
          .html-template .fs-xs { font-size: 5.4pt; }
          .html-template .fs-sm { font-size: 6.2pt; }
          .html-template .fs-md { font-size: 7.2pt; }
          .html-template .fs-lg { font-size: 8pt; }
          .html-template .fs-xl { font-size: 9pt; }
          .html-template .fs-xxl { font-size: 10pt; }
          .html-template .lh-08 { line-height: .8; }
          .html-template .lh-09 { line-height: .9; }
          .html-template .lh-10 { line-height: 1; }
          .html-template .lh-11 { line-height: 1.1; }
          .html-template .lh-12 { line-height: 1.2; }
          .html-template .lh-14 { line-height: 1.4; }
          .html-template .pt-1 { padding-top: 1mm; }
          .html-template .pt-2 { padding-top: 2mm; }
          .html-template .pt-3 { padding-top: 3mm; }
          .html-template .pb-1 { padding-bottom: 1mm; }
          .html-template .pb-2 { padding-bottom: 2mm; }
          .html-template .pb-3 { padding-bottom: 3mm; }
          .html-template .mt-1 { margin-top: 1mm; }
          .html-template .mt-2 { margin-top: 2mm; }
          .html-template .mt-3 { margin-top: 3mm; }
          .html-template .mb-1 { margin-bottom: 1mm; }
          .html-template .mb-2 { margin-bottom: 2mm; }
          .html-template .mb-3 { margin-bottom: 3mm; }
          .html-template .spacer-1 { height: 1mm; flex: 0 0 1mm; }
          .html-template .spacer-2 { height: 2mm; flex: 0 0 2mm; }
          .html-template .spacer-3 { height: 3mm; flex: 0 0 3mm; }
          .html-template .fw-400 { font-weight: 400; }
          .html-template .fw-500 { font-weight: 500; }
          .html-template .fw-600 { font-weight: 600; }
          .html-template .fw-700 { font-weight: 700; }
          .html-template .fw-800 { font-weight: 800; }
          .html-template .fw-900 { font-weight: 900; }
          .html-template .fw-950 { font-weight: 950; }
          .html-template .main { font-size: 9pt; font-weight: 900; }
          .html-template .sub { font-size: 7.2pt; font-weight: 800; }
          .html-template .qty { font-size: 8pt; font-weight: 900; }
          .html-template .small { font-size: 6.2pt; }
          .html-template .center { text-align: center; }
          .html-template .right { text-align: right; }
          .html-template .row { display: flex; gap: 2mm; align-items: baseline; justify-content: space-between; }
          .html-template .row-left { display: flex; gap: 2mm; align-items: baseline; justify-content: flex-start; }
          .html-template .row-center { display: flex; gap: 2mm; align-items: baseline; justify-content: center; }
          .html-template .v-top { margin-top: 0; }
          .html-template .template-barcode { position: static; height: 6mm; width: 100%; margin-top: 0; overflow: hidden; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="sheet">
          <div class="label">
            ${renderStyledLaserLabelHtml({ line1, line2, line3, barcodeText }, style, cfg)}
          </div>
        </div>
        <script>
          window.onload = function() {
            window.focus();
            window.print();
          };
        </script>
      </body>
    </html>
  `);
  win.document.close();
}

export function printLaserSheet({ jobs = [], layout = {}, startSlot = 1 } = {}) {
  const cfg = {
    columns: 3,
    rows: 11,
    labelsPerSheet: 33,
    pageWidthMm: 210,
    pageHeightMm: 297,
    marginLeftMm: 6,
    marginTopMm: 8.8,
    labelWidthMm: 66,
    labelHeightMm: 25.4,
    pitchXmm: 66,
    pitchYmm: 25.4,
    ...layout,
  };
  const firstSlot = Math.max(1, Math.min(Number(cfg.labelsPerSheet || 33), Number(startSlot || 1)));
  const labelsPerSheet = Math.max(1, Number(cfg.labelsPerSheet || (Number(cfg.columns || 3) * Number(cfg.rows || 11))));
  const normalizedJobs = Array.isArray(jobs) ? jobs : [];
  const pages = [];
  let jobIndex = 0;
  let pageIndex = 0;
  while (jobIndex < normalizedJobs.length) {
    const pageStartSlot = pageIndex === 0 ? firstSlot : 1;
    const pageCapacity = labelsPerSheet - pageStartSlot + 1;
    const pageJobs = normalizedJobs.slice(jobIndex, jobIndex + pageCapacity);
    const labels = pageJobs.map((job, index) => {
      const slot = pageStartSlot + index;
      const zero = slot - 1;
      const col = zero % cfg.columns;
      const row = Math.floor(zero / cfg.columns);
      const left = cfg.marginLeftMm + (col * cfg.pitchXmm);
      const top = cfg.marginTopMm + (row * cfg.pitchYmm);
      return `
        <div class="label" style="left:${left}mm;top:${top}mm;width:${cfg.labelWidthMm}mm;height:${cfg.labelHeightMm}mm;">
          ${renderStyledLaserLabelHtml(job.payload || {}, job.style || {}, cfg)}
        </div>
      `;
    }).join('');
    pages.push(`<div class="page">${labels}</div>`);
    jobIndex += pageJobs.length;
    pageIndex += 1;
  }
  const pageHtml = pages.join('');

  const win = window.open('', 'fvs-laser-sheet-print', 'width=920,height=720');
  if (!win) throw new Error('Popup blocked. Allow popups to print laser sheet.');
  win.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>FVS ST-33 Sheet Print</title>
        <style>
          @page { size: A4; margin: 0; }
          * { box-sizing: border-box; }
          html, body {
            margin: 0;
            padding: 0;
            width: ${cfg.pageWidthMm}mm;
            min-height: ${cfg.pageHeightMm}mm;
            font-family: Arial, Helvetica, sans-serif;
            color: #111827;
          }
          .page {
            position: relative;
            width: ${cfg.pageWidthMm}mm;
            height: ${cfg.pageHeightMm}mm;
            page-break-after: always;
          }
          .page:last-child {
            page-break-after: auto;
          }
          .label {
            position: absolute;
            overflow: hidden;
          }
          .laser-line {
            position: absolute;
            font-weight: 700;
            line-height: 1;
            white-space: nowrap;
            overflow: hidden;
          }
          .bars {
            position: absolute;
            overflow: hidden;
          }
          .barcode-svg { display: block; width: 100%; height: 100%; fill: #111827; }
          .html-template {
            position: absolute;
            inset: 0;
            padding: 1.6mm 2mm;
            font-weight: 800;
            line-height: 1.05;
            overflow: hidden;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .html-template .line { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .html-template > * { margin-top: 0; margin-bottom: 0; }
          .html-template .fs-6 { font-size: 6pt; }
          .html-template .fs-7 { font-size: 7pt; }
          .html-template .fs-8 { font-size: 8pt; }
          .html-template .fs-9 { font-size: 9pt; }
          .html-template .fs-10 { font-size: 10pt; }
          .html-template .fs-11 { font-size: 11pt; }
          .html-template .fs-12 { font-size: 12pt; }
          .html-template .fs-14 { font-size: 14pt; }
          .html-template .fs-16 { font-size: 16pt; }
          .html-template .fs-18 { font-size: 18pt; }
          .html-template .fs-xs { font-size: 5.4pt; }
          .html-template .fs-sm { font-size: 6.2pt; }
          .html-template .fs-md { font-size: 7.2pt; }
          .html-template .fs-lg { font-size: 8pt; }
          .html-template .fs-xl { font-size: 9pt; }
          .html-template .fs-xxl { font-size: 10pt; }
          .html-template .lh-08 { line-height: .8; }
          .html-template .lh-09 { line-height: .9; }
          .html-template .lh-10 { line-height: 1; }
          .html-template .lh-11 { line-height: 1.1; }
          .html-template .lh-12 { line-height: 1.2; }
          .html-template .lh-14 { line-height: 1.4; }
          .html-template .pt-1 { padding-top: 1mm; }
          .html-template .pt-2 { padding-top: 2mm; }
          .html-template .pt-3 { padding-top: 3mm; }
          .html-template .pb-1 { padding-bottom: 1mm; }
          .html-template .pb-2 { padding-bottom: 2mm; }
          .html-template .pb-3 { padding-bottom: 3mm; }
          .html-template .mt-1 { margin-top: 1mm; }
          .html-template .mt-2 { margin-top: 2mm; }
          .html-template .mt-3 { margin-top: 3mm; }
          .html-template .mb-1 { margin-bottom: 1mm; }
          .html-template .mb-2 { margin-bottom: 2mm; }
          .html-template .mb-3 { margin-bottom: 3mm; }
          .html-template .spacer-1 { height: 1mm; flex: 0 0 1mm; }
          .html-template .spacer-2 { height: 2mm; flex: 0 0 2mm; }
          .html-template .spacer-3 { height: 3mm; flex: 0 0 3mm; }
          .html-template .fw-400 { font-weight: 400; }
          .html-template .fw-500 { font-weight: 500; }
          .html-template .fw-600 { font-weight: 600; }
          .html-template .fw-700 { font-weight: 700; }
          .html-template .fw-800 { font-weight: 800; }
          .html-template .fw-900 { font-weight: 900; }
          .html-template .fw-950 { font-weight: 950; }
          .html-template .main { font-size: 9pt; font-weight: 900; }
          .html-template .sub { font-size: 7.2pt; font-weight: 800; }
          .html-template .qty { font-size: 8pt; font-weight: 900; }
          .html-template .small { font-size: 6.2pt; }
          .html-template .center { text-align: center; }
          .html-template .right { text-align: right; }
          .html-template .row { display: flex; gap: 2mm; align-items: baseline; justify-content: space-between; }
          .html-template .row-left { display: flex; gap: 2mm; align-items: baseline; justify-content: flex-start; }
          .html-template .row-center { display: flex; gap: 2mm; align-items: baseline; justify-content: center; }
          .html-template .v-top { margin-top: 0; }
          .html-template .template-barcode { position: static; height: 6mm; width: 100%; margin-top: 0; overflow: hidden; }
          @media screen {
            body { background: #e5e7eb; padding: 12px; }
            .page { background: #fff; box-shadow: 0 10px 30px rgba(15,23,42,.18); margin-bottom: 12px; }
            .label { outline: 1px dashed rgba(15,23,42,.18); }
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .label { outline: none; }
          }
        </style>
      </head>
      <body>
        ${pageHtml}
        <script>
          window.onload = function() {
            window.focus();
            window.print();
          };
        </script>
      </body>
    </html>
  `);
  win.document.close();
}


function isLikelyProductSku(value) {
  const v = String(value || '').trim();
  if (!v) return false;
  if (/^\d+$/.test(v)) return false;
  return /[A-Za-z]/.test(v);
}

function getPickSheetProductSku(job = {}) {
  const action = String(job?.meta?.action || '').trim();
  const payload = job?.payload || {};
  const candidates = action === 'mark_used'
    ? [job?.meta?.sku, payload.line2, payload.barcodeText, payload.line1]
    : [job?.meta?.sku, payload.line1, payload.barcodeText, payload.line2];
  const likely = candidates.find(isLikelyProductSku);
  if (likely) return String(likely).trim();
  const fallback = candidates.find((value) => String(value || '').trim());
  return String(fallback || '').trim();
}

export function printLabelPickSheetPdf({ jobs = [], startSlot = 1 } = {}) {
  const list = Array.isArray(jobs) ? jobs : [];
  if (list.length === 0) throw new Error('No labels selected for pick sheet.');
  const generatedAt = new Date().toLocaleString();
  const labelsPerSheet = 33;
  const firstSlot = Math.max(1, Math.min(labelsPerSheet, Number(startSlot || 1)));
  const rows = list.map((job, index) => {
    const payload = job?.payload || {};
    const meta = job?.meta || {};
    const absoluteOffset = (firstSlot - 1) + index;
    const slot = (absoluteOffset % labelsPerSheet) + 1;
    const imageUrl = String(meta.productImageUrl || meta.imageUrl || '').trim();
    const sku = getPickSheetProductSku(job);
    const qty = String(meta.qty != null ? meta.qty : String(payload.line3 || '').replace(/m$/i, '')).trim();
    const pieceId = String(meta.pieceId || '').trim();
    const orderId = String(meta.orderDisplayId || meta.orderNumber || '').trim();
    const action = String(meta.action || '').replace(/_/g, ' ').trim();
    return `
      <tr>
        <td class="slot">${slot}</td>
        <td class="image-cell">
          ${imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(sku)}" />` : '<div class="image-empty">No image</div>'}
        </td>
        <td class="sku-cell">
          <strong>${escapeHtml(sku || '-')}</strong>
        </td>
        <td class="qty-cell">${escapeHtml(qty ? `${qty}m` : '-')}</td>
        <td>${escapeHtml(pieceId || '-')}</td>
        <td>${escapeHtml(orderId || '-')}</td>
        <td>${escapeHtml(action || '-')}</td>
      </tr>
    `;
  }).join('');

  const win = window.open('', 'fvs-label-pick-sheet-pdf', 'width=920,height=720');
  if (!win) throw new Error('Popup blocked. Allow popups to generate pick sheet PDF.');
  win.document.write(`
    <!doctype html>
    <html>
      <head>
        <title>FVS Label Pick Sheet</title>
        <style>
          @page { size: A4; margin: 10mm; }
          * { box-sizing: border-box; }
          body {
            margin: 0;
            font-family: Arial, Helvetica, sans-serif;
            color: #111827;
            background: #fff;
          }
          .header {
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: flex-start;
            margin-bottom: 8mm;
          }
          h1 {
            margin: 0 0 2mm;
            font-size: 16pt;
            line-height: 1;
          }
          .meta {
            color: #475569;
            font-size: 9pt;
            font-weight: 700;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }
          .col-slot { width: 12mm; }
          .col-image { width: 24mm; }
          .col-sku { width: 42mm; }
          .col-qty { width: 12mm; }
          .col-piece { width: 30mm; }
          .col-order { width: 28mm; }
          .col-type { width: 28mm; }
          th, td {
            border: 1px solid #cbd5e1;
            padding: 2mm;
            vertical-align: middle;
            font-size: 9pt;
          }
          th {
            background: #f1f5f9;
            color: #0f172a;
            text-align: left;
            font-size: 8pt;
            text-transform: uppercase;
            letter-spacing: .02em;
          }
          td strong {
            display: block;
            font-size: 10pt;
            line-height: 1.15;
          }
          .slot {
            text-align: center;
            font-size: 14pt;
            font-weight: 900;
          }
          .image-cell {
            height: 20mm;
            text-align: center;
          }
          .sku-cell {
            overflow-wrap: anywhere;
            word-break: break-word;
          }
          .qty-cell {
            text-align: center;
            white-space: nowrap;
          }
          .image-cell img {
            max-width: 18mm;
            max-height: 18mm;
            object-fit: cover;
            border: 1px solid #e2e8f0;
          }
          .image-empty {
            color: #94a3b8;
            font-size: 8pt;
            font-weight: 800;
          }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            tr { break-inside: avoid; page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>FVS Label Pick Sheet</h1>
            <div class="meta">Use this sheet to find fabric and apply labels in slot order.</div>
          </div>
          <div class="meta">Generated: ${escapeHtml(generatedAt)}<br />Labels: ${list.length}<br />Start slot: ${Number(startSlot || 1)}</div>
        </div>
        <table>
          <colgroup>
            <col class="col-slot" />
            <col class="col-image" />
            <col class="col-sku" />
            <col class="col-qty" />
            <col class="col-piece" />
            <col class="col-order" />
            <col class="col-type" />
          </colgroup>
          <thead>
            <tr>
              <th>Slot</th>
              <th>Image</th>
              <th>SKU</th>
              <th>Qty</th>
              <th>Piece ID</th>
              <th>Order</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <script>
          window.onload = function() {
            window.focus();
            window.print();
          };
        </script>
      </body>
    </html>
  `);
  win.document.close();
}

const DEFAULT_PRINT_STICKER_STYLE = {
  labelWidth: 406,
  labelHeight: 203,
  marginX: 16,
  line1Y: 18,
  line2Y: 58,
  line3Y: 96,
  line1FontSize: 30,
  line2FontSize: 26,
  line3FontSize: 30,
  textAlign: 'left',
  textCase: 'as_typed',
  barcodeY: 132,
  barcodeHeight: 60,
  barcodeModuleWidth: 2,
  barcodeAlign: 'left',
  showBarcodeText: true,
  orientation: 'normal',
};

function clampNumber(value, fallback, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.round(n)));
}

function sanitizeAlign(value, fallback = 'left') {
  const v = String(value || '').trim().toLowerCase();
  return ['left', 'center', 'right'].includes(v) ? v : fallback;
}

export function sanitizePrintStickerStyle(input = {}) {
  const src = input && typeof input === 'object' ? input : {};
  const d = DEFAULT_PRINT_STICKER_STYLE;
  return {
    labelWidth: clampNumber(src.labelWidth, d.labelWidth, 200, 900),
    labelHeight: clampNumber(src.labelHeight, d.labelHeight, 100, 600),
    marginX: clampNumber(src.marginX, d.marginX, 0, 120),
    line1Y: clampNumber(src.line1Y, d.line1Y, 0, 550),
    line2Y: clampNumber(src.line2Y, d.line2Y, 0, 550),
    line3Y: clampNumber(src.line3Y, d.line3Y, 0, 550),
    line1FontSize: clampNumber(src.line1FontSize, d.line1FontSize, 10, 80),
    line2FontSize: clampNumber(src.line2FontSize, d.line2FontSize, 10, 80),
    line3FontSize: clampNumber(src.line3FontSize, d.line3FontSize, 10, 80),
    textAlign: sanitizeAlign(src.textAlign, d.textAlign),
    textCase: String(src.textCase || d.textCase).toLowerCase() === 'uppercase' ? 'uppercase' : 'as_typed',
    barcodeY: clampNumber(src.barcodeY, d.barcodeY, 0, 550),
    barcodeHeight: clampNumber(src.barcodeHeight, d.barcodeHeight, 20, 200),
    barcodeModuleWidth: clampNumber(src.barcodeModuleWidth, d.barcodeModuleWidth, 1, 6),
    barcodeAlign: sanitizeAlign(src.barcodeAlign, d.barcodeAlign),
    showBarcodeText: src.showBarcodeText == null ? d.showBarcodeText : !!src.showBarcodeText,
    orientation: String(src.orientation || d.orientation).toLowerCase() === 'inverted' ? 'inverted' : 'normal',
  };
}

function applyTextCase(value, style) {
  const text = String(value || '');
  return style.textCase === 'uppercase' ? text.toUpperCase() : text;
}

function zplAlign(align) {
  return { center: 'C', right: 'R' }[align] || 'L';
}

function barcodeXFor(barcodeText, style) {
  if (style.barcodeAlign === 'left') return style.marginX;
  const contentWidth = Math.max(40, style.labelWidth - (style.marginX * 2));
  const estimatedWidth = Math.min(
    contentWidth,
    Math.max(80, String(barcodeText || '').length * style.barcodeModuleWidth * 11)
  );
  if (style.barcodeAlign === 'right') return Math.max(0, style.labelWidth - style.marginX - estimatedWidth);
  return Math.max(0, Math.round((style.labelWidth - estimatedWidth) / 2));
}

// ── Client-side ZPL builder (mirrors backend buildZpl2x1) ──

export function buildZpl2x1({ line1, line2, line3, barcodeText } = {}, inputStyle = {}) {
  const style = sanitizePrintStickerStyle(inputStyle);
  const l1 = applyTextCase(line1, style).slice(0, 28);
  const l2 = applyTextCase(line2, style).slice(0, 32);
  const l3 = applyTextCase(line3, style).slice(0, 24);
  const bc = applyTextCase(barcodeText, style).slice(0, 48);
  const textWidth = Math.max(40, style.labelWidth - (style.marginX * 2));
  const align = zplAlign(style.textAlign);
  const barcodeX = barcodeXFor(bc, style);
  const barcodeTextFlag = style.showBarcodeText ? 'Y' : 'N';

  return [
    '^XA',
    style.orientation === 'inverted' ? '^POI' : '^PON',
    `^PW${style.labelWidth}`,
    `^LL${style.labelHeight}`,
    '^CI28',
    `^CF0,${style.line1FontSize}`,
    `^FO${style.marginX},${style.line1Y}^FB${textWidth},1,0,${align},0^FD${l1}^FS`,
    `^CF0,${style.line2FontSize}`,
    `^FO${style.marginX},${style.line2Y}^FB${textWidth},1,0,${align},0^FD${l2}^FS`,
    `^CF0,${style.line3FontSize}`,
    `^FO${style.marginX},${style.line3Y}^FB${textWidth},1,0,${align},0^FD${l3}^FS`,
    `^BY${style.barcodeModuleWidth},2,${style.barcodeHeight}`,
    `^FO${barcodeX},${style.barcodeY}^BCN,${style.barcodeHeight},${barcodeTextFlag},N,N^FD${bc}^FS`,
    '^XZ',
  ].join('\n');
}

// ── localStorage config ──

const CONFIG_KEY = 'fvs:qzTray:config';

const DEFAULT_CONFIG = {
  enabled: false,
  printers: { STICKER: '' },
  printerMode: 'STICKER',
  autoConnect: false,
};

export function loadQzConfig() {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    if (!raw) return { ...DEFAULT_CONFIG, printers: { ...DEFAULT_CONFIG.printers } };
    const parsed = JSON.parse(raw);
    return {
      enabled: !!parsed.enabled,
      printers: { STICKER: '', ...parsed.printers },
      printerMode: parsed.printerMode === 'LASER' ? 'LASER' : 'STICKER',
      autoConnect: !!parsed.autoConnect,
    };
  } catch {
    return { ...DEFAULT_CONFIG, printers: { ...DEFAULT_CONFIG.printers } };
  }
}

export function saveQzConfig(cfg) {
  localStorage.setItem(CONFIG_KEY, JSON.stringify(cfg));
}
